import React from 'react';
import Icon from '../../../components/AppIcon';

const StaffAvailabilityPanel = ({ staff }) => {
  const getStatusColor = (status) => {
    const colorMap = {
      available: 'success',
      busy: 'warning',
      offline: 'muted-foreground'
    };
    return colorMap?.[status] || 'muted-foreground';
  };

  const getStatusBg = (status) => {
    const bgMap = {
      available: 'bg-success/10 text-success border-success/20',
      busy: 'bg-warning/10 text-warning border-warning/20',
      offline: 'bg-muted text-muted-foreground border-border'
    };
    return bgMap?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const lastActive = new Date(timestamp);
    const diffMs = now - lastActive;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Active now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return 'Offline';
  };

  const availableStaff = staff?.filter(s => s?.status === 'available')?.length || 0;
  const busyStaff = staff?.filter(s => s?.status === 'busy')?.length || 0;
  const offlineStaff = staff?.filter(s => s?.status === 'offline')?.length || 0;

  return (
    <div className="card">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground mb-1">Staff Availability</h2>
        <p className="text-sm text-muted-foreground">
          Real-time status and response tracking
        </p>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="p-3 rounded-lg bg-success/10 border border-success/20">
          <p className="text-2xl font-bold text-success">{availableStaff}</p>
          <p className="text-xs text-success mt-1">Available</p>
        </div>
        <div className="p-3 rounded-lg bg-warning/10 border border-warning/20">
          <p className="text-2xl font-bold text-warning">{busyStaff}</p>
          <p className="text-xs text-warning mt-1">Busy</p>
        </div>
        <div className="p-3 rounded-lg bg-muted border border-border">
          <p className="text-2xl font-bold text-muted-foreground">{offlineStaff}</p>
          <p className="text-xs text-muted-foreground mt-1">Offline</p>
        </div>
      </div>

      {/* Staff List */}
      <div className="space-y-3">
        {staff?.map((member) => (
          <div
            key={member?.id}
            className="p-4 rounded-lg border border-border hover:shadow-md transition-all"
          >
            {/* Staff Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={20} className="text-primary" />
                  </div>
                  <div
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card bg-${getStatusColor(member?.status)}`}
                  />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{member?.name}</h3>
                  <p className="text-xs text-muted-foreground capitalize">{member?.role}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBg(member?.status)}`}>
                {member?.status}
              </span>
            </div>

            {/* Staff Metrics */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="p-2 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground">Current Alerts</p>
                <p className="text-lg font-bold text-foreground">{member?.currentAlerts}</p>
              </div>
              <div className="p-2 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground">Avg Response</p>
                <p className="text-lg font-bold text-foreground">{member?.avgResponseTime}</p>
              </div>
            </div>

            {/* Contact Preferences */}
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs text-muted-foreground">Contact:</p>
              <div className="flex items-center gap-1">
                {member?.preferences?.includes('sms') && (
                  <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center">
                    <Icon name="MessageSquare" size={12} className="text-primary" />
                  </div>
                )}
                {member?.preferences?.includes('email') && (
                  <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center">
                    <Icon name="Mail" size={12} className="text-primary" />
                  </div>
                )}
                {member?.preferences?.includes('push') && (
                  <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center">
                    <Icon name="Bell" size={12} className="text-primary" />
                  </div>
                )}
              </div>
            </div>

            {/* Last Active */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Icon name="Clock" size={12} />
              <span>{getTimeAgo(member?.lastActive)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaffAvailabilityPanel;